import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { MyAccess } from '../../access/model/types'
import type { ConnectionFormRegistry } from '../../resources/model/connectionForms'
import { DIAGNOSTICS_CAPABILITY, makeSelectResourcesWithCapability } from '../../resources/model/selectors'
import type { ResourceSummary } from '../../resources/model/types'
import type { DiagnosticsVm, ServiceReadiness, ServiceVersion } from './types'

export interface DiagnosticsVmInput {
  version: QueryResultLike<ServiceVersion>
  ready: QueryResultLike<ServiceReadiness>
  resources: QueryResultLike<readonly ResourceSummary[]>
  access: QueryResultLike<MyAccess>
  forms: ConnectionFormRegistry
}

function viewOf<T>(pick: (input: DiagnosticsVmInput) => QueryResultLike<T>, isEmpty: (data: T) => boolean) {
  return createSelector(
    [
      (input: DiagnosticsVmInput) => pick(input).isUninitialized,
      (input: DiagnosticsVmInput) => pick(input).isLoading,
      (input: DiagnosticsVmInput) => pick(input).isError,
      (input: DiagnosticsVmInput) => pick(input).isSuccess,
      (input: DiagnosticsVmInput) => pick(input).error,
      (input: DiagnosticsVmInput) => pick(input).data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        isEmpty,
      ),
  )
}

export const makeSelectDiagnosticsVm = () => {
  const selectVersionView = viewOf((input) => input.version, () => false)
  const selectReadyView = viewOf((input) => input.ready, () => false)
  const selectResourcesView = viewOf((input) => input.resources, () => false)
  const selectDiagnosable = makeSelectResourcesWithCapability()
  const selectRows = createSelector(
    [
      (input: DiagnosticsVmInput) =>
        selectDiagnosable({ resources: input.resources.data, access: input.access, capability: DIAGNOSTICS_CAPABILITY }),
      (input: DiagnosticsVmInput) => input.forms,
    ],
    (resources, forms) =>
      resources.map((resource) => ({ id: resource.id, name: resource.displayName, kindTitle: forms.titleOf(resource.kind) })),
  )

  return createSelector(
    [
      selectVersionView,
      (input: DiagnosticsVmInput) => input.version.data,
      selectReadyView,
      (input: DiagnosticsVmInput) => input.ready.data,
      selectResourcesView,
      selectRows,
    ],
    (versionView, version, readyView, ready, resourcesView, rows): DiagnosticsVm => ({
      gateway: {
        version: { query: versionView, value: versionView.status === 'ready' ? (version ?? null) : null },
        // 503 `/ready` — это ответ «не готов», а не «неизвестно»: transport отдаёт его видом `unavailable`.
        ready: {
          query: readyView,
          ready: readyView.status === 'ready' ? true : readyView.status === 'unavailable' ? false : null,
          status: readyView.status === 'ready' ? (ready?.status ?? null) : (readyView.error?.message ?? null),
        },
      },
      resources: { query: resourcesView, rows },
    }),
  )
}
